import { useEffect, useState, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles, ZoomIn } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface AvisoCampanha {
  id: string;
  titulo: string;
  mensagem: string;
  anexo_url?: string | null;
  anexo_nome?: string | null;
  data_inicio: string;
  data_fim?: string | null;
  criado_por_nome?: string;
}

interface Props {
  avisos: AvisoCampanha[];
  onClose: () => void;
  onVisualizar: (avisoId: string) => void;
}

/**
 * Modal fullscreen do Modo Destaque.
 *
 * Layout: imagem grande no topo (max 60vh) + título + texto formatado abaixo,
 * com scroll suave. Suporta navegação entre múltiplas campanhas ativas.
 *
 * Comportamento:
 * - Auto-abre toda vez que o usuário entra no CRM enquanto houver campanhas ativas.
 * - Botão "Fechar" apenas oculta nesta sessão (volta na próxima abertura).
 * - Persistência é controlada pelo admin via `status` / `data_fim` do aviso.
 */
export function AvisoCampanhaFullscreen({ avisos, onClose, onVisualizar }: Props) {
  const [idx, setIdx] = useState(0);
  const [zoomAtivo, setZoomAtivo] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const visualizadosRef = useRef<Set<string>>(new Set());
  const total = avisos.length;
  const aviso = avisos[idx];

  useEffect(() => {
    if (aviso?.id && !visualizadosRef.current.has(aviso.id)) {
      visualizadosRef.current.add(aviso.id);
      onVisualizar(aviso.id);
    }
  }, [aviso?.id, onVisualizar]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // ESC fecha
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight' && idx < total - 1) setIdx(idx + 1);
      else if (e.key === 'ArrowLeft' && idx > 0) setIdx(idx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose, idx, total]);

  // Bloqueia scroll do body enquanto aberto
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!aviso) return null;

  const dataLabel = (() => {
    try {
      return format(new Date(aviso.data_inicio), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch {
      return '';
    }
  })();

  return (
    <div className="fixed inset-0 z-[10300] flex flex-col bg-gradient-to-br from-brand-vinho via-[#1a0202] to-[#0a0000] animate-in fade-in duration-300">
      {/* Backdrop com brilho dourado sutil */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background: 'radial-gradient(ellipse at top, rgba(240,196,25,0.18) 0%, transparent 60%)',
        }}
      />

      {/* Header fixo */}
      <div className="relative z-10 flex items-center justify-between px-4 md:px-6 py-3 border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F0C419] to-[#E5A800] px-3 py-1 text-[11px] font-black uppercase tracking-wider text-brand-vinho shadow-lg shadow-warning/40">
            <Sparkles className="h-3 w-3" />
            Modo destaque
          </div>
          {total > 1 && (
            <span className="text-xs text-white/60 font-medium">
              {idx + 1} de {total}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {total > 1 && (
            <>
              <button
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={idx === 0}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-card/10 transition disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Anterior"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                disabled={idx === total - 1}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-card/10 transition disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Próxima"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
          <button
            onClick={handleClose}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-card/10 transition"
            aria-label="Fechar (ESC)"
            title="Fechar este destaque"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Corpo com scroll */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-4 md:px-8 py-6 md:py-8">
          {/* Imagem topo — efeito "vem pra frente" no hover/clique (sem modal, sem zoom focal) */}
          {aviso.anexo_url ? (
            <div
              className="mb-6 flex justify-center"
              style={{ perspective: '1200px' }}
            >
              <div
                className="group relative inline-block cursor-pointer transition-all duration-500 ease-out"
                onClick={() => setZoomAtivo((z) => !z)}
                title={zoomAtivo ? 'Clique para reduzir' : 'Clique para ampliar'}
                style={{
                  // Reserva espaço extra abaixo quando ampliado para não invadir o título
                  marginBottom: zoomAtivo ? '4rem' : '0',
                  marginTop: zoomAtivo ? '1rem' : '0',
                }}
              >
                <img
                  ref={imgRef}
                  src={aviso.anexo_url}
                  alt={aviso.titulo}
                  className={cn(
                    'block w-auto max-w-full select-none rounded-2xl ring-1 ring-warning/20',
                    'transition-all duration-500 ease-out will-change-transform',
                    zoomAtivo
                      ? 'max-h-[78vh] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] ring-2 ring-warning/60'
                      : 'max-h-[60vh] scale-100 shadow-2xl group-hover:scale-[1.04] group-hover:shadow-[0_25px_70px_-20px_rgba(0,0,0,0.75)] group-hover:ring-warning/40',
                  )}
                  draggable={false}
                />
              </div>
            </div>
          ) : null}

          {/* Dica sutil abaixo da imagem (fora dela, não atrapalha o visual) */}
          {aviso.anexo_url && (
            <p className="mb-6 -mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/40 font-medium">
              <ZoomIn className="h-3 w-3" />
              {zoomAtivo ? 'Clique na imagem para reduzir' : 'Clique na imagem para ampliar'}
            </p>
          )}

          {!aviso.anexo_url && (
            <div className="mb-6 flex h-48 items-center justify-center rounded-2xl bg-card/5 border border-white/10">
              <Sparkles className="h-16 w-16 text-warning/40" />
            </div>
          )}

          {/* Título */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#F0C419] leading-tight tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            {aviso.titulo}
          </h1>
          {dataLabel && (
            <p className="mt-2 text-sm text-white/50 font-medium uppercase tracking-widest">
              {dataLabel}
              {aviso.criado_por_nome && (
                <span className="ml-2 text-white/40 normal-case tracking-normal">· por {aviso.criado_por_nome}</span>
              )}
            </p>
          )}

          {/* Texto da mensagem (preserva emojis e quebras) */}
          <div className="mt-6 text-base md:text-lg leading-relaxed text-white/90 whitespace-pre-wrap font-medium">
            {aviso.mensagem}
          </div>

          {/* Espaçador para não colar no rodapé */}
          <div className="h-8" />
        </div>
      </div>

      {/* Footer fixo */}
      <div className="relative z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm px-4 md:px-6 py-3">
        <div className="mx-auto max-w-4xl flex items-center justify-between gap-3">
          <p className="text-[11px] text-white/50 hidden sm:block">
            {aviso.data_fim
              ? `Disponível até ${format(new Date(aviso.data_fim), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}.`
              : 'Sem data final definida.'}
          </p>
          <button
            onClick={handleClose}
            className={cn(
              'ml-auto inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-bold transition',
              'bg-gradient-to-r from-[#F0C419] to-[#E5A800] text-brand-vinho shadow-lg shadow-warning/40',
              'hover:from-[#FFD42A] hover:to-[#F0C419] hover:scale-[1.02] active:scale-[0.98]',
            )}
          >
            Fechar
            <span className="text-[10px] opacity-70">(ESC)</span>
          </button>
        </div>
      </div>
    </div>
  );
}
